import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { ISupportThread, ISupportMessage } from 'app/models/support';
import * as Actions from './actions';

export const stateName = 'support';

function lastMessageMs(t: ISupportThread): number {
  const d = t.lastMessageAt ?? t.updatedAt ?? t.createdAt;
  if (!d) return 0;
  return d instanceof Date ? d.getTime() : new Date(d).getTime();
}

export const threadAdapter: EntityAdapter<ISupportThread> = createEntityAdapter<ISupportThread>({
  selectId: (t) => t.id ?? '',
  sortComparer: (a, b) => lastMessageMs(b) - lastMessageMs(a),
});

export const messageAdapter: EntityAdapter<ISupportMessage> = createEntityAdapter<ISupportMessage>({
  selectId: (m) => m.id ?? '',
  sortComparer: (a, b) => {
    const ad = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt as any).getTime();
    const bd = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt as any).getTime();
    return ad - bd;
  },
});

export interface State {
  threads: EntityState<ISupportThread>;
  messages: EntityState<ISupportMessage>;
  selectedThreadId: string | null;
  loadingThreads: boolean;
  loadingMessagesForThread: string | null;
  error: any;
}

export const initialState: State = {
  threads: threadAdapter.getInitialState(),
  messages: messageAdapter.getInitialState(),
  selectedThreadId: null,
  loadingThreads: false,
  loadingMessagesForThread: null,
  error: null,
};

export const reducer = createReducer<State>(
  initialState,
  on(Actions.clearAllItems, () => initialState),

  on(Actions.loadUserThreadsFromState, (state) => ({
    ...state,
    loadingThreads: true,
    error: null,
  })),
  on(Actions.loadUserThreadsSuccess, (state, { items }) => ({
    ...state,
    loadingThreads: false,
    threads: threadAdapter.setAll(items, state.threads),
  })),
  on(Actions.loadUserThreadsFailure, (state, { error }) => ({
    ...state,
    loadingThreads: false,
    error,
  })),

  on(Actions.loadThreadSuccess, (state, { item }) => ({
    ...state,
    threads: threadAdapter.upsertOne(item, state.threads),
  })),

  on(Actions.addThreadSuccess, (state, { item }) => ({
    ...state,
    threads: threadAdapter.upsertOne(item, state.threads),
  })),

  on(Actions.assignToMeSuccess, (state, { id, uid, email }) => ({
    ...state,
    threads: threadAdapter.updateOne(
      { id, changes: { assignedTo: uid, assignedToEmail: email } },
      state.threads,
    ),
  })),

  on(Actions.archiveThreadSuccess, (state, { id, archived }) => ({
    ...state,
    threads: threadAdapter.updateOne({ id, changes: { archived } }, state.threads),
  })),

  on(Actions.setSelectedThread, (state, { id }) => ({
    ...state,
    selectedThreadId: id,
  })),

  on(Actions.loadMessages, (state, { threadId }) => ({
    ...state,
    loadingMessagesForThread: threadId,
  })),
  on(Actions.loadMessagesSuccess, (state, { threadId, items }) => ({
    ...state,
    loadingMessagesForThread: null,
    messages: messageAdapter.setAll(
      [
        ...Object.values(state.messages.entities).filter(
          (m): m is ISupportMessage => !!m && m.threadId !== threadId,
        ),
        ...items,
      ],
      state.messages,
    ),
  })),
  on(Actions.loadMessagesFailure, (state, { error }) => ({
    ...state,
    loadingMessagesForThread: null,
    error,
  })),

  on(Actions.addMessageSuccess, (state, { item, threadId, threadChanges }) => {
    const messages = messageAdapter.upsertOne(item, state.messages);
    if (!threadChanges) {
      return { ...state, messages };
    }
    return {
      ...state,
      messages,
      threads: threadAdapter.updateOne(
        { id: threadId, changes: threadChanges },
        state.threads,
      ),
    };
  }),

  on(Actions.deleteThreadSuccess, (state, { id }) => {
    const remainingMessages = Object.values(state.messages.entities).filter(
      (m): m is ISupportMessage => !!m && m.threadId !== id,
    );
    return {
      ...state,
      threads: threadAdapter.removeOne(id, state.threads),
      messages: messageAdapter.setAll(remainingMessages, state.messages),
      selectedThreadId: state.selectedThreadId === id ? null : state.selectedThreadId,
    };
  }),

  on(Actions.deleteMessageSuccess, (state, { id }) => ({
    ...state,
    messages: messageAdapter.removeOne(id, state.messages),
  })),
);

const getState = createFeatureSelector<State>(stateName);
const threadSelectors = threadAdapter.getSelectors();
const messageSelectors = messageAdapter.getSelectors();

export const selectAllThreads = createSelector(getState, (s) =>
  threadSelectors.selectAll(s.threads),
);
export const selectThreadEntities = createSelector(getState, (s) =>
  threadSelectors.selectEntities(s.threads),
);
export const selectSelectedThreadId = createSelector(getState, (s) => s.selectedThreadId);
export const selectSelectedThread = createSelector(
  selectThreadEntities,
  selectSelectedThreadId,
  (entities, id) => (id ? entities[id] : null),
);
export const selectThread = (id: string) =>
  createSelector(selectThreadEntities, (entities) => entities[id]);

export const selectAllMessages = createSelector(getState, (s) =>
  messageSelectors.selectAll(s.messages),
);
export const selectMessagesForThread = (threadId: string) =>
  createSelector(selectAllMessages, (msgs) =>
    msgs.filter((m) => m.threadId === threadId),
  );
export const selectMessagesForSelectedThread = createSelector(
  selectAllMessages,
  selectSelectedThreadId,
  (msgs, id) => (id ? msgs.filter((m) => m.threadId === id) : []),
);

export const selectLoadingThreads = createSelector(getState, (s) => s.loadingThreads);
export const selectError = createSelector(getState, (s) => s.error);

export const selectActiveThreads = createSelector(selectAllThreads, (threads) =>
  threads.filter((t) => !t.archived),
);
export const selectArchivedThreads = createSelector(selectAllThreads, (threads) =>
  threads.filter((t) => !!t.archived),
);

export const selectUnreadByUserCount = createSelector(selectActiveThreads, (threads) =>
  threads.filter((t) => t.unreadByUser).length,
);

export const selectUnreadByDevCount = createSelector(selectActiveThreads, (threads) =>
  threads.filter((t) => t.unreadByDev).length,
);
